import { Button, Modal, Stack, TextInput } from "@mantine/core";
import { useForm, zodResolver } from "@mantine/form";
import { z } from "zod";
import { useEffect, useState } from "react";

// Enums
import { EEntity, ESize } from "@enums/";

// Constants
import {
  KEY_GET_ALL_BENEFITS_FOLDERS,
  KEY_GET_ALL_REQUIREMENTS_FOLDERS,
  MIN_THREE_CHARACTERS,
  NOT_EMPTY,
} from "@constants/";

// Utils
import { success } from "@utils/";

// Hooks
import { useInvalidate } from "@hooks/";

// Service
import { updateFolder } from "@api/";

type TFolderEdit = z.infer<typeof schema>;

type TData = {
  id: number;
  name: string;
  folder_type: EEntity;
};

enum ELoginFormFields {
  NAME = "name",
  FOLDER_TYPE = "folder_type",
}

const schema = z.object({
  [ELoginFormFields.NAME]: z.string({ required_error: NOT_EMPTY }).min(3, { message: MIN_THREE_CHARACTERS }),
  [ELoginFormFields.FOLDER_TYPE]: z.number(),
});

interface IEditFolderModalProps {
  opened: boolean;
  entity: EEntity;
  data: TData;
  close: () => void;
}

const EditFolderModal = ({ opened, close, entity, data }: IEditFolderModalProps) => {
  const { invalidate } = useInvalidate();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const form = useForm({
    initialValues: {
      [ELoginFormFields.NAME]: "",
      [ELoginFormFields.FOLDER_TYPE]: entity,
    },
    validate: zodResolver(schema),
  });

  useEffect(() => {
    if (!data) return;
    form.setValues({
      [ELoginFormFields.NAME]: data.name,
    });
  }, [data]);

  const onSubmit = async (values: TFolderEdit) => {
    setIsSubmitting(true);
    const response = await updateFolder(String(data.id), values);

    if (response) {
      success({ title: "Success", message: response?.message });

      if (entity === EEntity.BENEFITS) {
        invalidate(KEY_GET_ALL_BENEFITS_FOLDERS);
      } else {
        invalidate(KEY_GET_ALL_REQUIREMENTS_FOLDERS);
      }

      setIsSubmitting(false);
      close();
    }
  };

  return (
    <Modal radius={ESize.MD} centered size={ESize.MD} opened={opened} onClose={close} title="Edit Folder">
      <Stack gap={ESize.MD}>
        <form
          className="flex h-full w-full flex-col gap-y-md"
          onSubmit={form.onSubmit((values) => {
            onSubmit(values);
          })}
        >
          <TextInput label="Folder Name" {...form.getInputProps(ELoginFormFields.NAME)} />

          <Button loading={isSubmitting} fullWidth type="submit">
            Update
          </Button>
        </form>
      </Stack>
    </Modal>
  );
};

export { EditFolderModal };
